// Cloud voice-note transcription (#393): the extension side.
//
// The Transcribe buttons try the cloud first when the backend says the feature is on for
// this user and the workspace has a credit. Any refusal or failure falls back to the free
// on-device Whisper path, so the cloud can only ever add a result, never remove one.
//
// Only the two Transcribe buttons opt in. The automatic transcription of incoming voice notes
// in AI-linked chats stays on-device: it must never spend credits or send audio without a click.
//
// This module holds the decisions (pure, with injected ports) plus the real ports.

import axios from 'axios';
// The singleton the app's i18next config initializes; importing it here has no side effects.
import i18n from 'i18next';
import {
  CLOUD_TRANSCRIPTION_COMMANDS,
  CLOUD_TRANSCRIPTION_MAX_BYTES,
  CLOUD_TRANSCRIPTION_MAX_SECONDS,
  cloudTranscriptionCreditCost,
  CloudTranscribeErrorDataSchema,
  CloudTranscribeResponseSchema,
  CloudTranscriptionStatusResponseSchema,
  type CloudTranscribeErrorReason,
  type CloudTranscribeRequest,
  type CloudTranscribeResponse,
  type CloudTranscriptionPricing,
  type CloudTranscriptionStatusResponse,
} from '@bt-common/interfaces/cloud-transcription.interface';
import type { TranscriptionResult } from '@bt-common/interfaces/speech-contracts.interface';

/** A voice note's raw bytes, as the page primitive ships them. */
export interface VoiceNoteAudio {
  audioBase64: string;
  mimetype: string;
  /** Seconds, from WhatsApp's own message metadata, when it has one. */
  durationSec?: number;
}

export type CloudTranscribeCallResult =
  | { ok: true; response: CloudTranscribeResponse }
  | { ok: false; reason: CloudTranscribeErrorReason | 'network' };

export interface CloudTranscriptionPorts {
  /** Null when the status could not be read (network, auth, old backend). */
  status(): Promise<CloudTranscriptionStatusResponse | null>;
  /** Null when the bytes could not be fetched or are over `maxBytes`. */
  fetchAudio(messageKey: string, maxBytes: number): Promise<VoiceNoteAudio | null>;
  transcribe(req: CloudTranscribeRequest): Promise<CloudTranscribeCallResult>;
}

/** Why the cloud did not produce the result, when the device did. */
export type CloudSkipReason =
  /** Cloud is not offered to this user (flag off, backend without the feature): say nothing. */
  | 'disabled'
  /** This note is over the cloud's length or size limit. */
  | 'too_long'
  /** The cloud does not take this audio format. */
  | 'unsupported'
  /** The workspace has no credit left. */
  | 'no_credits'
  /** Cloud was on but failed this time (audio unavailable, rate limit, engine down, network). */
  | 'unavailable';

export type CloudAttempt =
  | { kind: 'cloud'; response: CloudTranscribeResponse }
  | { kind: 'skipped'; why: CloudSkipReason };

export interface CloudAttemptInput {
  messageKey: string;
  /** The resolved language, or undefined for auto-detect. */
  languageHint?: string;
  /** Called once the audio is in hand, right before it is sent, with what it will cost. */
  onUpload?: (credits: number) => void;
}

/** The status's pricing; a backend that predates per-block pricing charged `creditCost` flat. */
export function pricingOf(status: CloudTranscriptionStatusResponse): CloudTranscriptionPricing {
  return status.pricing ?? { creditsPerBlock: status.creditCost, blockSeconds: 0 };
}

/** Translate under `cloudTranscription.*`; English when i18n isn't loaded (or lacks the key). */
function tr(key: string, english: string, opts?: Record<string, string | number>): string {
  if (!i18n.isInitialized) return english;
  const fullKey = `cloudTranscription.${key}`;
  const out = i18n.t(fullKey, { defaultValue: english, ...(opts ?? {}) });
  return typeof out === 'string' && out.length > 0 && out !== fullKey ? out : english;
}

/** "1 credit", "3 credits". */
export function creditsLabel(n: number): string {
  return tr('credits', `${n} credit${n === 1 ? '' : 's'}`, { count: n });
}

/** Turn a refusal into the reason the device path is shown with. */
export function skipReasonFor(reason: CloudTranscribeErrorReason | 'network'): CloudSkipReason {
  switch (reason) {
    case 'insufficient_credits':
      return 'no_credits';
    case 'disabled':
      return 'disabled';
    // Retrying will never help these; the user is told why the device did it instead.
    case 'too_long':
    case 'too_large':
      return 'too_long';
    case 'unsupported_audio':
      return 'unsupported';
    default:
      return 'unavailable';
  }
}

/**
 * Try the cloud once. Never throws: every failure becomes a `skipped` outcome so the caller
 * runs the device path. Reads nothing beyond the status call when the flag is off.
 */
export async function tryCloudTranscription(
  ports: CloudTranscriptionPorts,
  input: CloudAttemptInput,
): Promise<CloudAttempt> {
  try {
    const status = await ports.status();
    // No readable status (e.g. a backend without the feature) = treat as off: never promise a retry.
    if (!status) return { kind: 'skipped', why: 'disabled' };
    if (!status.enabled) return { kind: 'skipped', why: 'disabled' };
    // An unreadable balance is left to the server, which knows the real number.
    if (status.creditsRemaining !== null && status.creditsRemaining < status.creditCost) {
      return { kind: 'skipped', why: 'no_credits' };
    }
    const audio = await ports.fetchAudio(input.messageKey, CLOUD_TRANSCRIPTION_MAX_BYTES);
    if (!audio) return { kind: 'skipped', why: 'unavailable' };
    // Known to be over the cap: don't upload a note the server will refuse.
    if (audio.durationSec !== undefined && audio.durationSec > CLOUD_TRANSCRIPTION_MAX_SECONDS) {
      return { kind: 'skipped', why: 'too_long' };
    }
    const cost = cloudTranscriptionCreditCost(audio.durationSec ?? 0, pricingOf(status));
    if (status.creditsRemaining !== null && status.creditsRemaining < cost) return { kind: 'skipped', why: 'no_credits' };
    input.onUpload?.(cost);
    const result = await ports.transcribe({
      messageKey: input.messageKey,
      audioBase64: audio.audioBase64,
      mimetype: audio.mimetype,
      ...(audio.durationSec !== undefined ? { durationSec: audio.durationSec } : {}),
      ...(input.languageHint ? { languageHint: input.languageHint } : {}),
    });
    if (result.ok) return { kind: 'cloud', response: result.response };
    return { kind: 'skipped', why: skipReasonFor(result.reason) };
  } catch {
    return { kind: 'skipped', why: 'unavailable' };
  }
}

// ── What the buttons show ──────────────────────────────────────────

/** The shape the Transcribe buttons render (the thunk's result). */
export interface TranscribeOutcome {
  text: string | null;
  language?: string | null;
  transcription?: TranscriptionResult | null;
  /** Where the result came from. Absent on older callers = device. */
  engine?: 'cloud' | 'device';
  /** Cloud only. */
  creditsCharged?: number;
  /** Cloud only: served from the server's cache, no charge. */
  cached?: boolean;
  /** Device only: why the cloud was not used (when it was asked for). */
  cloudSkipped?: CloudSkipReason;
}

export const NO_SPEECH_TEXT = 'No speech detected in this recording.';
export const DEVICE_FAILED_TEXT =
  "Couldn't transcribe on this device: the speech model didn't load. A network filter can block its download.";

/** Why the cloud wasn't a way out either, appended to a failed device run. */
function deviceFailedHint(why: CloudSkipReason | undefined): string | null {
  switch (why) {
    case 'no_credits': return tr('hintNoCredits', "Cloud transcription needs credits, and this workspace doesn't have enough left.");
    case 'unavailable': return tr('hintUnavailable', 'Cloud transcription is unavailable right now. Please try again in a minute.');
    case 'too_long': return tr('hintTooLong', 'The recording is also too long for cloud transcription (5 minutes max).');
    case 'unsupported': return tr('hintUnsupported', "Cloud transcription can't take this file either.");
    default: return null;
  }
}

/**
 * The main text of a finished transcription. A failed device run (no structured result) is
 * an error, not "no speech": that mix-up is what customers saw when the model download was
 * blocked.
 */
export function transcriptionBody(outcome: TranscribeOutcome): string {
  const result = outcome.transcription ?? null;
  const noSpeech = tr('noSpeech', NO_SPEECH_TEXT);
  if (result?.flags.includes('silence')) return noSpeech;
  if (outcome.text) return outcome.text;
  if (result) return noSpeech;
  const failed = tr('deviceFailed', DEVICE_FAILED_TEXT);
  const hint = deviceFailedHint(outcome.cloudSkipped);
  return hint ? `${failed} ${hint}` : failed;
}

/** True when the device path ran and produced nothing at all (the model never loaded). */
export function isDeviceFailure(outcome: TranscribeOutcome): boolean {
  return outcome.engine !== 'cloud' && !outcome.text && !outcome.transcription;
}

/** Extra bits for the detail line: what the cloud charged, or why it wasn't used. */
export function cloudDetailBits(outcome: TranscribeOutcome): string[] {
  if (outcome.engine === 'cloud') {
    const cloud = tr('detailCloud', 'Cloud');
    if (outcome.cached) return [cloud, tr('detailCached', 'cached, no charge')];
    const n = outcome.creditsCharged ?? 0;
    return n > 0 ? [cloud, creditsLabel(n)] : [cloud, tr('detailNoCharge', 'no charge')];
  }
  if (isDeviceFailure(outcome)) return [];
  switch (outcome.cloudSkipped) {
    case 'no_credits': return [tr('skippedNoCredits', 'Out of credits: transcribed on this device')];
    case 'too_long': return [tr('skippedTooLong', 'Too long for cloud transcription: transcribed on this device')];
    case 'unsupported': return [tr('skippedUnsupported', "Cloud can't take this file: transcribed on this device")];
    default: return [];
  }
}

/** The progress phase the thunk announces while the cloud call is in flight. */
export const CLOUD_PROGRESS_PHASE = 'cloud-transcribing';
/** What the buttons show while the cloud call is in flight, with its cost when known. */
export function cloudProgressText(credits?: number): string {
  if (!credits || credits <= 0) return tr('progress', 'Transcribing in the cloud…');
  const label = creditsLabel(credits);
  return tr('progressWithCost', `Transcribing in the cloud (${label})…`, { credits: label });
}

/** The credits a progress event carries (the event detail is untyped DOM data). */
export function progressCredits(detail: unknown): number | undefined {
  if (typeof detail !== 'object' || detail === null || !('credits' in detail)) return undefined;
  return typeof detail.credits === 'number' ? detail.credits : undefined;
}

// ── Real ports ─────────────────────────────────────────────────────

/** The `data` a FeathersError carries on the wire, when it is one of ours. */
export function refusalReasonFromError(err: unknown): CloudTranscribeErrorReason | 'network' {
  if (!axios.isAxiosError(err)) return 'network';
  const body: unknown = err.response?.data;
  if (typeof body !== 'object' || body === null || !('data' in body)) return 'network';
  const parsed = CloudTranscribeErrorDataSchema.safeParse(body.data);
  return parsed.success ? parsed.data.reason : 'network';
}

async function postAction(cmd: string, body: object): Promise<unknown> {
  const { apiCall } = await import('@extension/wa-communication-layer/api/api-call-helper');
  return apiCall<unknown>('post', `/actions?cmd=${encodeURIComponent(cmd)}`, body);
}

/** Status is cheap but not free: reuse an answer for a minute. */
const STATUS_TTL_MS = 60_000;
let statusCache: { at: number; value: CloudTranscriptionStatusResponse } | null = null;

export function resetCloudStatusCache(): void {
  statusCache = null;
}

export function createLivePorts(
  fetchAudio: CloudTranscriptionPorts['fetchAudio'],
  now: () => number = Date.now,
): CloudTranscriptionPorts {
  return {
    async status() {
      if (statusCache && now() - statusCache.at < STATUS_TTL_MS) return statusCache.value;
      try {
        const parsed = CloudTranscriptionStatusResponseSchema.safeParse(
          await postAction(CLOUD_TRANSCRIPTION_COMMANDS.status, {}),
        );
        if (!parsed.success) return null;
        statusCache = { at: now(), value: parsed.data };
        return parsed.data;
      } catch {
        return null;
      }
    },
    fetchAudio,
    async transcribe(req) {
      try {
        const parsed = CloudTranscribeResponseSchema.safeParse(
          await postAction(CLOUD_TRANSCRIPTION_COMMANDS.transcribe, req),
        );
        if (!parsed.success) return { ok: false, reason: 'network' };
        // The server's balance is fresher than the cached status.
        if (statusCache) statusCache = { at: now(), value: { ...statusCache.value, creditsRemaining: parsed.data.creditsRemaining } };
        return { ok: true, response: parsed.data };
      } catch (err) {
        const reason = refusalReasonFromError(err);
        if (reason === 'insufficient_credits' && statusCache) {
          statusCache = { at: now(), value: { ...statusCache.value, creditsRemaining: 0 } };
        }
        return { ok: false, reason };
      }
    },
  };
}
